import { Box, Stack, Typography, Divider } from "@mui/material";
import {
  Security,
  Info,
  Cookie,
  Link,
  Update,
  Shield,
  Language,
  ContactMail,
} from "@mui/icons-material";
import useDocumentMeta from "../../utils/useDocumentMeta";

const PrivacyPolicy = () => {
  useDocumentMeta(
    "Privacy Policy | Radiance Aesthetics | Hindley | Wigan",
    "How Radiance Aesthetics handles data and cookies on our website, and your rights as a visitor to our Hindley, Wigan clinic site.",
  );

  const privacyItems = [
    {
      icon: <Info sx={{ color: "var(--color-text-muted)", fontSize: 20 }} />,
      title: "Introduction",
      content:
        "This privacy policy describes how Radiance Aesthetics handles information when you visit our website. We do not have a contact form, so we never collect information you type in on this site — but we do use analytics and advertising cookies, described below, to understand how the site is used.",
    },
    {
      icon: <Shield sx={{ color: "#4caf50", fontSize: 20 }} />,
      title: "Data Collection",
      content:
        "This website has no contact form, account sign-up, or newsletter sign-up, so we never collect personal details you type in. If you click a 'Book Now' button you are taken to our booking partner, Timely, on their own website, where they collect the details needed to make your appointment under their own privacy policy.",
    },
    {
      icon: <Cookie sx={{ color: "#ff9800", fontSize: 20 }} />,
      title: "Cookies",
      content:
        "Alongside essential cookies for site functionality, this website uses Google Analytics 4 cookies to understand how visitors use the site, and a Google Ads conversion cookie to measure how many visitors click through to book an appointment. These cookies are set by Google and are covered by Google's privacy policy.",
    },
    {
      icon: <Language sx={{ color: "#2196f3", fontSize: 20 }} />,
      title: "Website Analytics",
      content:
        "We use Google Analytics 4 and Vercel Analytics to understand general usage patterns — such as which pages are viewed, which buttons are clicked, and which treatments generate the most interest — so we can improve the site. This data is reported in aggregate and is not used to identify individual visitors.",
    },
    {
      icon: <Link sx={{ color: "#9c27b0", fontSize: 20 }} />,
      title: "External Links",
      content:
        "Our website may contain links to external websites, including our booking system. Please note that we have no control over external websites and are not responsible for their privacy practices. We recommend reviewing their privacy policies before providing any information.",
    },
    {
      icon: <Security sx={{ color: "#f44336", fontSize: 20 }} />,
      title: "Data Security",
      content:
        "We do not operate a database of visitor or client personal data — this website has no contact form or account system, and nothing you type is ever sent to our servers. Analytics data is processed by Google and Vercel under their own security standards.",
    },
    {
      icon: <ContactMail sx={{ color: "#607d8b", fontSize: 20 }} />,
      title: "Contact Information",
      content:
        "If you have any questions about this privacy policy or our data practices, please contact us through the contact information provided on our website. Any communication will be handled in accordance with applicable privacy laws.",
    },
    {
      icon: <Update sx={{ color: "#009688", fontSize: 20 }} />,
      title: "Policy Updates",
      content:
        "This privacy policy may be updated from time to time to reflect changes in our practices or legal requirements. The latest version will always be published on this page. Last updated: July 2026.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: "var(--color-bg-warm)",
        padding: { xs: "40px 16px", md: "64px 24px" },
        minHeight: "60vh",
      }}
    >
      <Stack
        sx={{
          maxWidth: "720px",
          margin: "0 auto",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: "var(--color-accent)",
            textAlign: "center",
            mb: 1.5,
          }}
        >
          Legal
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-primary)",
            fontWeight: 400,
            textAlign: "center",
            fontSize: { xs: "28px", md: "40px" },
            mb: 1.5,
          }}
          variant="h1"
        >
          Privacy Policy
        </Typography>

        <Typography
          sx={{
            color: "var(--color-text-light)",
            textAlign: "center",
            fontSize: "16px",
            mb: 5,
            maxWidth: "600px",
            lineHeight: 1.7,
            fontFamily: "var(--font-body)",
          }}
        >
          Your privacy is important to us. Learn how we protect your
          information.
        </Typography>

        <Stack
          gap={0}
          sx={{
            width: "100%",
            backgroundColor: "var(--color-white)",
            borderRadius: "8px",
            border: "1px solid var(--color-border)",
            overflow: "hidden",
          }}
        >
          {privacyItems.map((item, index) => (
            <Box key={index}>
              <Stack
                direction="row"
                gap={2}
                sx={{
                  alignItems: "flex-start",
                  p: { xs: 2.5, md: 3 },
                }}
              >
                <Box sx={{ mt: 0.25, flexShrink: 0 }}>{item.icon}</Box>
                <Stack gap={0.75} sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "var(--color-primary)",
                      fontFamily: "var(--font-body)",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: 1.7,
                      color: "var(--color-text-light)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {item.content}
                  </Typography>
                </Stack>
              </Stack>
              {index < privacyItems.length - 1 && (
                <Divider sx={{ opacity: 0.5 }} />
              )}
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 4,
            p: 2,
            backgroundColor: "var(--color-white)",
            borderRadius: "8px",
            width: "100%",
            border: "1px solid var(--color-border)",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "var(--color-text-muted)",
              fontStyle: "italic",
              textAlign: "center",
              fontFamily: "var(--font-body)",
            }}
          >
            This website has no contact form and does not collect information
            you type in. It does use Google Analytics and Google Ads cookies to
            understand site usage, as described above.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default PrivacyPolicy;
